<script lang="ts">
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import ArrowRightIcon from "@lucide/svelte/icons/arrow-right";
	import PhoneIcon from "@lucide/svelte/icons/phone";
	import {
		getTemplateContext,
		cloudinaryUrl,
		cloudinarySrcset
	} from "../template-context.svelte";
	import { page } from "$app/state";

	const templateContext = getTemplateContext();

	let {
		slogan = templateContext.siteSlogan,
		title = "We Buy Houses For Cash, As-Is & Now",
		subTitle = "No showings. No agents. No repairs. No fees.",
		imageUrl = templateContext.images.teamHero
	}: {
		slogan?: string;
		title?: string;
		subTitle?: string;
		imageUrl?: string;
	} = $props();

	const currentSearchParams = page.url.searchParams;
	const offerUrl = $derived(`/get-your-offer?${currentSearchParams.toString()}`);
</script>

<div class="relative">
	<div class="mx-auto max-w-7xl">
		<div class="relative z-10 lg:w-full lg:max-w-2xl">
			<svg
				viewBox="0 0 100 100"
				preserveAspectRatio="none"
				aria-hidden="true"
				class="absolute inset-y-0 right-8 hidden h-full w-70 translate-x-1/2 transform fill-white lg:block dark:fill-gray-900"
			>
				<polygon points="0,0 90,0 50,100 0,100" />
			</svg>

			<div class="relative px-6 py-8 sm:py-32 lg:px-8 lg:py-32 lg:pr-0">
				<div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl">
					<div class="hidden sm:mb-10 sm:flex">
						<div
							class="text-primary relative rounded-full px-3 py-1 text-sm/6 font-bold ring-1 ring-gray-900/10 hover:ring-gray-900/20 dark:text-gray-400 dark:ring-white/10 dark:hover:ring-white/20"
						>
							{slogan}
						</div>
					</div>
					<h1
						class="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-6xl dark:text-white"
					>
						{title}
					</h1>
					<p
						class="mt-8 text-lg font-medium text-pretty text-gray-500 sm:text-xl/8 dark:text-gray-400"
					>
						{subTitle}
					</p>
					<div class="mt-6 flex flex-col items-center gap-4 sm:mt-10 sm:gap-6">
						<Button
							href={offerUrl}
							class="bg-primary text-primary-foreground hover:bg-primary/90 h-14 w-full rounded-xl text-lg font-bold shadow-lg transition-all hover:shadow-xl sm:h-16 sm:text-xl"
						>
							Get My Cash Offer
							<ArrowRightIcon class="ml-2" />
						</Button>
						<a
							href={`tel:${templateContext.contact.phone}`}
							class="border-foreground/20 text-foreground hover:bg-foreground/5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border text-base font-semibold transition-colors"
						>
							<PhoneIcon class="size-5" />
							{templateContext.contact.phone}
						</a>
						<p class="text-muted-foreground text-center text-sm">
							Free, no obligation. Takes 30 seconds. No showings needed.
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="bg-gray-50 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 dark:bg-gray-800">
		<img
			src={cloudinaryUrl(imageUrl, { width: 1280 })}
			srcset={`${cloudinaryUrl(imageUrl, { width: 412 })} 412w, ${cloudinaryUrl(imageUrl, { width: 640 })} 640w, ${cloudinaryUrl(imageUrl, { width: 960 })} 960w, ${cloudinaryUrl(imageUrl, { width: 1280 })} 1280w`}
			alt="Home buying options - Fast cash offers and equity protection"
			class="aspect-[3/2] object-cover lg:aspect-auto lg:size-full"
			width="1280"
			height="854"
			sizes="(min-width: 1024px) 50vw, 100vw"
			loading="eager"
			fetchpriority="high"
		/>
	</div>
</div>
